import { Link, Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router'
import { LogOut, ShieldCheck } from 'lucide-react'

import { LoginGate } from './components/LoginGate'
import { useAuth } from './lib/auth'
import { AuditPage } from './routes/AuditPage'
import { DashboardPage } from './routes/DashboardPage'
import { SettingsPage } from './routes/SettingsPage'
import { SyncPage } from './routes/SyncPage'
import { TrademarksPage } from './routes/TrademarksPage'
import { UsersPage } from './routes/UsersPage'

function RootLayout() {
  const auth = useAuth()

  if (auth.status !== 'authenticated') {
    return <LoginGate />
  }

  const user = auth.user

  return (
    <div className="appShell">
      <aside className="sidebar">
        <div className="brand">
          <ShieldCheck size={22} aria-hidden="true" />
          <span>MarkLens</span>
        </div>
        <nav className="navList" aria-label="Main">
          <Link to="/">
            Screening
          </Link>
          <Link to="/trademarks">Trademarks</Link>
          <Link to="/sync">Sync</Link>
          <Link to="/audit">Audit</Link>
          <Link to="/users">Users</Link>
          <Link to="/settings">Settings</Link>
        </nav>
        <div className="sidebarUser">
          <div>
            <span>{user.email}</span>
            <small>{user.role}</small>
          </div>
          <button
            aria-label="Sign out"
            className="sidebarIconButton"
            type="button"
            onClick={() => {
              void auth.signOut()
            }}
          >
            <LogOut size={18} aria-hidden="true" />
          </button>
        </div>
      </aside>
      <main className="workspace">
        <Outlet />
      </main>
    </div>
  )
}

const rootRoute = createRootRoute({
  component: RootLayout
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage
})

const caseDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cases/$caseId',
  component: CaseDashboardRoute
})

function CaseDashboardRoute() {
  const { caseId } = caseDashboardRoute.useParams()
  return <DashboardPage initialCaseId={caseId} />
}

const trademarksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trademarks',
  component: TrademarksPage
})

const syncRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sync',
  component: SyncPage
})

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: AuditPage
})

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  component: UsersPage
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage
})

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  caseDashboardRoute,
  trademarksRoute,
  syncRoute,
  auditRoute,
  usersRoute,
  settingsRoute
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
